<template>
  <v-dialog
    v-model="open"
    :fullscreen="smAndDown"
    :max-width="smAndDown ? null : maxWidth"
    :persistent="true"
  >
    <v-card v-if="open" ref="modal" style="min-height: 500px;">
      <v-toolbar
        color="primary"
        dark
        max-height="64"
        @mousedown="startDrag($event)"
        class="draggable-modal"
      >
        <v-toolbar-title style="min-width: fit-content">
          {{ modalTitle }}
        </v-toolbar-title>
        <v-spacer />
  <v-btn icon dark @click="closeCameraController" data-testid="close-button">
          <v-icon large>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <div>
        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate/>
        </div>
        <v-alert
          v-else-if="!camera"
          variant="outlined"
          prominent
          density="compact"
          type="warning"
          class="m-2"
        >
          <template v-slot:title>{{ t('camera_not_supported') }}</template>
          {{ t('are_you_seeing_this_message_in_error') }}
        </v-alert>
        <CameraLogin v-else :camera="camera">
          <ThumbnailSlider
            :camera="camera"
            v-model="selectedCamera"
            disableErrorMessage
            @loaded="showVideo = true"
            @change="handleChangeThumbnailLoaded"
          />
          <VideoController
            v-if="showVideo"
            :key="cameraKey"
            :camera="camera"
            :selectedCamera="selectedCamera"
            @reload-camera="loadCamera"
          />
        </CameraLogin>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import draggableModalMixin from '../mixins/draggableModalMixin';
export default {
  mixins: [draggableModalMixin],
};
</script>

<script setup>
import { ref, toRefs, computed, watch, onMounted } from 'vue';
import { createCameraInstance, loadCameraPending } from '../helpers/camera';
import CameraLogin from './CameraLogin.vue';
import VideoController from './VideoController.vue';
import ThumbnailSlider from './ThumbnailSlider.vue';
import { ProviderMap } from '../helpers/camera-utils';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const { smAndDown, width, thresholds, name: breakPointName } = useDisplay();
const emit = defineEmits(['update:event','doneLoading','camera-close']);
const props = defineProps({
  nsn: {
    type: [String, Number],
    required: true,
  },
  orgId: {
    type: String,
    required: true,
  },
  userId: {
    type: String,
    required: true,
  },
  event: {
    type: [Object, Boolean],
    validator: (value) => {
      // register optional, time is required
      // time in format 2024-08-26 05:05:52
      if (!value) return true;
      return value.hasOwnProperty('time');
    },
  },
});
const { nsn, orgId, userId, event } = toRefs(props);

const open = computed({
  get: () => !!event.value,
  set: (value) => emit('update:event', value),
});
const camera = ref();
const loading = ref(false);
const error = ref(false);
defineExpose({ loading, error });
const selectedCamera = ref();
const showVideo = ref(false);
const cameraKey = ref(0);

const modalTitle = computed(() => {
  if (camera.value?.cameraProviderName) {
    if (
      camera.value?.cameraProviderName === 'itech' ||
      camera.value?.cameraProviderName === 'ITECH'
    ) {
      return `3xLogic - ${nsn.value}`;
    } else if (
      camera.value?.cameraProviderName === 'UAS' &&
      camera.value?.camera?.camProvider
    ) {
      // handle Shore Systems, Itech Digital
      return `${camera.value?.camera?.camProvider} - ${nsn.value}`;
    } else {
      return `${camera.value?.cameraProviderName} - ${nsn.value}`;
    }
  } else {
    if (camera.value?.camera?.camProvider) {
      return `${camera.value?.camera?.camProvider} - ${nsn.value}`;
    } else {
      return `Camera - ${nsn.value}`;
    }
  }
});

function handleChangeThumbnailLoaded(cameraId) {
  selectedCamera.value = cameraId;
  showVideo.value = true;
}

async function closeCameraController() {
  open.value = false;
  try { emit('camera-close'); } catch (_) { }
}

async function loadCamera() {
  loading.value = true;
  error.value = false;
  selectedCamera.value = null;
  try {
    const obj = await loadCameraPending(nsn.value, orgId.value);
    if (obj === 'This nsn has no camera provider.') {
      loading.value = false;
    } else {
      camera.value = createCameraInstance(
        obj.camProvider,
        obj,
        orgId.value,
        userId.value,
      );
      camera.value.cameraProviderName =
        camera.value.cameraProviderName.toLowerCase();
      camera.value.cameraProviderName =
        ProviderMap[camera.value.cameraProviderName];
      if (camera.value.create) camera.value.create();
    }
  } catch (e) {
    loading.value = false;
    error.value = true;
  } finally {
    loading.value = false;
  }
  emit('doneLoading', false);
}

const maxWidth = computed(() => {
  let dialogWidth = 600;

  // We can keep the default width for login modal.
  if (camera.value?.isAuthenticated === false) {
    return dialogWidth;
  }

  switch (breakPointName.value) {
    case 'md':
      return dialogWidth;
    case 'xl':
    case 'xxl':
      if (thresholds.value && thresholds.value['xl']) {
        dialogWidth = thresholds.value['xl'] - (width.value < 2200 ? 100 : 0);
      }
      break;

    default:
      if (thresholds.value && thresholds.value[breakPointName.value]) {
        dialogWidth = thresholds.value[breakPointName.value];
      }
  }

  // Idea here is to allow receipt modal to be opened side by side with the video modal.
  // Receipts are fixed to 420 pixels of width.
  return dialogWidth - 420;
});

watch(nsn, loadCamera, { immediate: true });
watch(event, (val, oldVal) => {
  if (camera.value && val) {
    camera.value.event = val;
  }
  if (oldVal && !val) {
    // dialog closed
    emit('camera-close');
  }
}, { immediate: true });

// Additional watcher to set event when camera becomes available
watch(camera, (newCamera) => {
  if (newCamera && event.value) {
    newCamera.event = event.value;
  }
});

// onMounted(async () => {
  // // check the local storage for a camera object
  // if (DTIQAuthReturn.value) {
  //   // reload the camera from the api to see if we are authenticated
  //   const camera = JSON.parse(localStorage.getItem('DTIQCamera'));
  //   localStorage.removeItem('DTIQCamera');
  //   if (camera) {
  //     loading.value = true;
  //     try {
  //       const { request } = await loadCameraPending(camera.location);
  //       const cameraObj = await request;
  //       cameraObj.event = camera.event;
  //       cameraObject.value = cameraObj;
  //     } catch (e) {
  //       console.error(e);
  //     } finally {
  //       loading.value = false;
  //     }
  //   }
  // }
// });
</script>
<i18n>
  {
    "en": {
      "camera_not_supported": "Camera not supported.",
      "are_you_seeing_this_message_in_error": "Are you seeing this message in error? Please contact support."
    },
    "es": {
      "camera_not_supported": "Cámara no compatible.",
      "are_you_seeing_this_message_in_error": "¿Está viendo este mensaje por error? Por favor contacte al soporte."
    },
    "fr": {
      "camera_not_supported": "Caméra non prise en charge.",
      "are_you_seeing_this_message_in_error": "Voyez-vous ce message par erreur ? Veuillez contacter le support."
    },
    "fr-ca": {
      "camera_not_supported": "Caméra non prise en charge.",
      "are_you_seeing_this_message_in_error": "Voyez-vous ce message par erreur ? Veuillez contacter le support."
    }
  }
</i18n>
<style scoped lang="scss">
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.draggable-modal {
  cursor: grab;
  user-select: none;
  transition: transform 0.1s ease;
  position: relative;
}
.draggable-modal:active {
  cursor: grabbing;
}

// These allow you to click on something outside of the dialog
:deep(.v-dialog) {
  position: relative; /* or absolute, based on your layout */
}

:deep(.v-overlay__scrim) {
  pointer-events: none;
}
</style>
